/** Dedicated code+PKCE handoff. A CA21 bearer never belongs in a redirect URL. */
export type FacilitiesAuthorizationRequest = {
  client_id: 'criticalasset-facilities'; redirect_uri: string; state: string;
  code_challenge: string; code_challenge_method: 'S256'; company_id: string;
  building_id: string; response_type: 'code';
};
export type FacilitiesReturn = {kind:'none'} | {kind:'invalid'} | {kind:'request'; request:FacilitiesAuthorizationRequest};
export type FacilitiesCompanyContext = {
  accessToken?: unknown;
  user?: {id?: unknown; email?: unknown; companyId?: unknown} | null;
};
const callback = 'https://facilities.criticalasset.com/auth/ca21/callback';
const keys = ['client_id','redirect_uri','state','code_challenge','code_challenge_method','company_id','building_id','response_type'];
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function facilitiesReturnPath(search: string): string | null {
  const query=new URLSearchParams(search);
  if(query.getAll('return_url').length!==1)return null;
  const raw=query.get('return_url');
  return parseFacilitiesReturn(raw).kind==='request' ? raw : null;
}

export function facilitiesAuthorizeEndpoint(graphqlUri: string): string {
  const url=new URL(graphqlUri);
  if(!['/api','/graphql'].includes(url.pathname))throw new Error('Unsupported CA21 API path');
  url.pathname=url.pathname==='/api' ? '/app/auth/facilities/authorize' : '/auth/facilities/authorize';
  url.search='';url.hash='';
  return url.href;
}

/** Accept only the official company switch result for the same signed-in person and requested tenant. */
export function facilitiesCompanyBearer(
  context: FacilitiesCompanyContext | null | undefined,
  request: FacilitiesAuthorizationRequest,
  currentUser: {id?: unknown; email?: unknown} | null | undefined,
): string | null {
  const token=typeof context?.accessToken==='string' ? context.accessToken : '';
  const switched=context?.user;
  const currentId=typeof currentUser?.id==='string' ? currentUser.id : '';
  const currentEmail=typeof currentUser?.email==='string' ? currentUser.email.toLowerCase() : '';
  const switchedId=typeof switched?.id==='string' ? switched.id : '';
  const switchedEmail=typeof switched?.email==='string' ? switched.email.toLowerCase() : '';
  return token && currentId && switchedId===currentId && switchedEmail===currentEmail && switched?.companyId===request.company_id
    ? token
    : null;
}

export function parseFacilitiesReturn(raw: string | null, allowLocal = false): FacilitiesReturn {
  if (!raw?.startsWith('/auth/facilities')) return {kind:'none'};
  try {
    if (!raw.startsWith('/auth/facilities/authorize?') || raw.includes('\\') || /[\r\n\t]/.test(raw)) return {kind:'invalid'};
    const url = new URL(raw, 'https://ca21.invalid');
    if (url.pathname !== '/auth/facilities/authorize' || url.hash) return {kind:'invalid'};
    const q = url.searchParams;
    if (keys.some(k=>q.getAll(k).length!==1) || Array.from(q.keys()).some(k=>!keys.includes(k))) return {kind:'invalid'};
    const request = Object.fromEntries(q) as FacilitiesAuthorizationRequest;
    const redirectAllowed = request.redirect_uri===callback || (allowLocal && request.redirect_uri==='http://localhost:9120/auth/ca21/callback');
    if (request.client_id!=='criticalasset-facilities' || request.response_type!=='code' || !redirectAllowed ||
        request.code_challenge_method!=='S256' || !/^[A-Za-z0-9_-]{43}$/.test(request.code_challenge) ||
        !/^[A-Za-z0-9_-]{32,256}$/.test(request.state) || !uuid.test(request.company_id) || !uuid.test(request.building_id)) return {kind:'invalid'};
    return {kind:'request',request};
  } catch { return {kind:'invalid'}; }
}

export function facilitiesCodeRedirect(raw: unknown, request: FacilitiesAuthorizationRequest): string | null {
  try {
    if (typeof raw !== 'string' || raw.includes('\\') || /[\r\n\t]/.test(raw)) return null;
    const url = new URL(raw), expected = new URL(request.redirect_uri), q=url.searchParams;
    if (url.origin!==expected.origin || url.pathname!==expected.pathname || url.username || url.password || url.hash ||
        q.getAll('state').length!==1 || q.get('state')!==request.state || q.getAll('code').length!==1 ||
        !/^[A-Za-z0-9_-]{20,512}$/.test(q.get('code')??'') || Array.from(q.keys()).some(k=>!['state','code'].includes(k))) return null;
    return url.href;
  } catch { return null; }
}
